import { useEffect, useRef, useState } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { toLocalDateString } from "@src/packages/utils/date";
import { useCreatePatientNote, useUpdatePatientNote } from "./usePatientNotes";
import {
  savePatientNoteDraft,
  type PatientNoteDraftSubmission,
} from "../patient-note-draft";

/** Explicitly saves the patient's complete words, without extracting facts. */
export function usePatientNoteDraft() {
  const { tenantId, patientId } = useApiClient();
  const owner = `${tenantId}/${patientId ?? ""}`;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const [draft, setDraft] = useState({ owner, text: "", savedText: "" });
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const submission = useRef<PatientNoteDraftSubmission | null>(null);
  const inFlight = useRef(false);
  const createNote = useCreatePatientNote();
  const updateNote = useUpdatePatientNote();

  useEffect(() => {
    setDraft({ owner, text: "", savedText: "" });
    setFailed(false);
    setSaving(false);
    submission.current = null;
    inFlight.current = false;
  }, [owner]);

  // Hide the old account's words immediately, before the reset effect runs.
  const text = draft.owner === owner ? draft.text : "";
  const saved = !!text && draft.owner === owner && text === draft.savedText;
  const save = async () => {
    if (!patientId || !text.trim() || saved || inFlight.current) return;
    const savingOwner = owner;
    inFlight.current = true;
    setSaving(true);
    setFailed(false);
    const pending = submission.current ?? {
      create: {
        date: toLocalDateString(new Date()),
        text,
        idempotencyKey: createIdempotencyKey(),
      },
      saved: null,
    };
    submission.current = pending;
    try {
      await savePatientNoteDraft(
        pending,
        text,
        createNote.mutateAsync,
        updateNote.mutateAsync,
      );
      if (
        currentOwner.current === savingOwner &&
        submission.current === pending
      ) {
        setDraft({ owner, text, savedText: text });
      }
    } catch {
      if (
        currentOwner.current === savingOwner &&
        submission.current === pending
      ) {
        setFailed(true);
      }
    } finally {
      if (
        currentOwner.current === savingOwner &&
        submission.current === pending
      ) {
        inFlight.current = false;
        setSaving(false);
      }
    }
  };

  return {
    text,
    saved,
    failed: draft.owner === owner && failed,
    saving: draft.owner === owner && saving,
    canSave: !!patientId && !!text.trim() && !saved && !saving,
    needsSignIn: !patientId,
    onChangeText: (text: string) => {
      setDraft((previous) => ({
        owner,
        text,
        savedText: previous.owner === owner ? previous.savedText : "",
      }));
      setFailed(false);
    },
    save,
  };
}
